import type { ComponentType } from 'react';
import styled from 'styled-components';
import {
    BookingEngineField,
    DataTestId,
    PORTAL_CONTAINER_ID,
    SUGGESTED_CURRENCY_CODES,
    useIsMobile,
    TextKey,
    FeatureFlag,
} from '@mews-distributor/common';
import { CurrencySelect } from '@mews/business-components';
import { useFeatureFlagValue } from '@mews/framework/feature-flags';
import { useIsTextKey, useLocalizeText } from '@mews/framework/localization';
import { CurrencySelectDialogButton } from '@mews/gx-ui';
import {
    Appbar as BaseAppbar,
    Container,
    getToken,
    IconButton,
    IconName,
    InputValue,
    Selected,
    Stack,
} from '@optimus-web/core';

interface Currency {
    code: string;
    symbol: string;
}

interface Language {
    code: string;
    fallbackLanguageCode?: string | null;
    englishName: string;
    localName: string;
}

type LanguageChangeHandler = (languageCode?: InputValue | Selected) => void;
type CurrencyChangeHandler = (currency: string) => void;

interface LogoProps {
    image?: string;
    propertyName: string;
    imageProps?: { id?: string | null };
    isMobile?: boolean;
}

interface LanguageSelectorProps {
    languages: Language[];
    name: BookingEngineField;
    selected: string;
    onChange: LanguageChangeHandler;
    dataTestId: DataTestId;
    portalContainerId: string;
}

interface LanguageSelectDialogProps {
    languages: Language[];
    selectedLanguageCode: string;
    onChange: LanguageChangeHandler;
    dataTestId: DataTestId;
    portalContainerId: string;
    buttonAriaLabelText: string;
    dialogTitleText: string;
}

interface AppbarFactoryComponents {
    Logo?: ComponentType<LogoProps>;
    LanguageSelector?: ComponentType<LanguageSelectorProps>;
    LanguageSelectDialog?: ComponentType<LanguageSelectDialogProps>;
}

interface AppbarProps {
    currencies: Currency[];
    currency: string;
    language: string;
    languages: Language[];
    onClose: () => void;
    onCurrencyChange: CurrencyChangeHandler;
    onLanguageChange: LanguageChangeHandler;
    propertyImageId: string;
    paymentGatewayCurrencyCode: string;
    propertyName: string;
    showCloseButton: boolean;
    showCurrencySelect: boolean;
    showPropertyInfo: boolean;
    isFxMulticurrencyServiceEnabled: boolean;
    [key: string]: unknown;
}

/**
 * Used in old layouts with white header and grey AppCanvas
 * Default Appbar uses backgroundStaticSunken
 */
export const AppbarFlat = styled(BaseAppbar)`
    background-color: ${getToken('backgroundStaticFlat')};
`;

const appbarFactory =
    ({ Logo, LanguageSelector, LanguageSelectDialog }: AppbarFactoryComponents = {}) =>
    ({
        currencies,
        currency: selectedCurrency,
        language,
        languages,
        onClose,
        onCurrencyChange,
        onLanguageChange,
        propertyImageId,
        paymentGatewayCurrencyCode,
        propertyName,
        showCloseButton,
        showCurrencySelect,
        showPropertyInfo,
        isFxMulticurrencyServiceEnabled,
        ...other
    }: AppbarProps) => {
        const lt = useLocalizeText();
        const isMobile = useIsMobile();
        const isTextKey = useIsTextKey();

        const currencySearchEnabledAbTest = useFeatureFlagValue(FeatureFlag.currencySearchEnabledAbTest);
        const showSearchInputInCurrencySelectDialog = currencySearchEnabledAbTest === 'currencySelectWithSearch';

        const currenciesWithDescriptionAdded = currencies.map((currency) => {
            const currencyTextKey = `Currency${currency.code}`;

            return {
                code: currency.code,
                symbol: currency.symbol,
                description: isTextKey(currencyTextKey) ? lt({ textKey: currencyTextKey }) : null,
            };
        });

        const currencySelectDialogTexts = {
            dialogTitle: lt({ textKey: TextKey.SelectYourCurrency }),
            suggestedCurrenciesTitle: lt({
                textKey: TextKey.CurrencySelectDialogTitleSuggestedCurrencies,
            }),
            otherCurrenciesTitle: lt({
                textKey: TextKey.CurrencySelectDialogTitleAllCurrencies,
            }),
            propertyCurrencyLabel: lt({ textKey: TextKey.Property }),
            searchInputPlaceholder: lt({
                textKey: TextKey.CurrencySelectDialogSearchInputPlaceholder,
            }),
            searchInputAriaLabel: lt({
                textKey: TextKey.CurrencySelectDialogSearchInputAriaLabel,
            }),
            filteredCurrenciesAriaLabel: lt({
                textKey: TextKey.CurrencySelectDialogSearchFilteredCurrenciesAriaLabel,
            }),
            filteredCurrenciesEmptyStatePrimary: lt({
                textKey: TextKey.CurrencySelectDialogSearchEmptyStatePrimaryText,
            }),
            filteredCurrenciesEmptyStateSecondary: lt({
                textKey: TextKey.CurrencySelectDialogSearchEmptyStateSecondaryText,
            }),
            otherCurrenciesAriaLabel: lt({
                textKey: TextKey.CurrencySelectDialogOtherCurrenciesAriaLabel,
            }),
            suggestedCurrenciesAriaLabel: lt({
                textKey: TextKey.CurrencySelectDialogSuggestedCurrenciesAriaLabel,
            }),
            getFilteredCountForScreenReadersText: (count: number) =>
                lt({
                    textKey: TextKey.CurrencySelectDialogSearchCountForScreenReaders,
                    substitutes: { count },
                }),
        };

        return (
            <AppbarFlat
                hasBorder
                leftSide={
                    Logo != null && showPropertyInfo ? (
                        <Container phoneOnly={{ padding: '0' }} tabletUp={{ padding: ['0', '200'] }}>
                            <Logo
                                key="logo"
                                propertyName={propertyName}
                                imageProps={{ id: propertyImageId }}
                                isMobile={isMobile}
                            />
                        </Container>
                    ) : null
                }
                rightSide={
                    <Container phoneOnly={{ padding: '0' }} tabletUp={{ padding: ['0', '200'] }}>
                        <Stack spacing="200" itemAlignment="center">
                            {isFxMulticurrencyServiceEnabled ? (
                                <>
                                    {LanguageSelectDialog != null && (
                                        <LanguageSelectDialog
                                            key="languageSelector"
                                            languages={languages}
                                            selectedLanguageCode={language}
                                            onChange={onLanguageChange}
                                            dataTestId={DataTestId.LanguageSelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                            buttonAriaLabelText={lt({ textKey: TextKey.SelectYourLanguage })}
                                            dialogTitleText={lt({ textKey: TextKey.SelectYourLanguage })}
                                        />
                                    )}

                                    {showCurrencySelect && (
                                        <CurrencySelectDialogButton
                                            key="currencySelect"
                                            currencies={currenciesWithDescriptionAdded}
                                            onChange={onCurrencyChange}
                                            selectedCurrencyCode={selectedCurrency}
                                            dataTestId={DataTestId.CurrencySelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                            suggestedCurrencyCodes={SUGGESTED_CURRENCY_CODES}
                                            propertyCurrencyCode={paymentGatewayCurrencyCode}
                                            showSearchInput={showSearchInputInCurrencySelectDialog}
                                            buttonAriaLabel={lt({ textKey: TextKey.SelectYourCurrency })}
                                            dialogTexts={currencySelectDialogTexts}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    {LanguageSelector != null && (
                                        <LanguageSelector
                                            key="languageSelector"
                                            languages={languages}
                                            name={BookingEngineField.LanguageSelector}
                                            selected={language}
                                            onChange={onLanguageChange}
                                            dataTestId={DataTestId.LanguageSelector}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                        />
                                    )}

                                    {showCurrencySelect && (
                                        <CurrencySelect
                                            key="currencySelect"
                                            currencies={currencies}
                                            onChange={onCurrencyChange}
                                            selected={selectedCurrency}
                                            dataTestId={DataTestId.CurrencySelector}
                                            name={BookingEngineField.CurrencySelect}
                                            portalContainerId={PORTAL_CONTAINER_ID}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    </Container>
                }
                rightActions={
                    showCloseButton ? (
                        <IconButton
                            key="closeButton"
                            icon={IconName.CrossClose}
                            onClick={onClose}
                            variant="ghost"
                            accessibleText={lt({ textKey: TextKey.Close })}
                            type="submit"
                        />
                    ) : null
                }
                {...other}
            />
        );
    };

export { appbarFactory };
export const Appbar = appbarFactory();
